/**
 * Firestore real-time listeners for session and comment updates
 *
 * Provides onSnapshot() listeners for instant updates instead of polling.
 * Falls back to REST API polling if Firestore fails.
 */

import {
    doc,
    collection,
    query,
    orderBy,
    where,
    onSnapshot,
    Unsubscribe,
    Timestamp,
    DocumentData,
    limit,
} from 'firebase/firestore';
import { getFirestoreDb } from '../config/firebase';
import { Session, Comment, CommentStatus, AccountInfo, PendingComment } from './api';

/**
 * Posting queue metadata stored in instagramAccounts/{accountId}.postingQueueMeta
 */
export interface PostingQueueMeta {
    isPostingQueueRunning: boolean;
    status: string;  // 'idle', 'running', 'needs_login', 'error', etc.
    vncUrl?: string;
    vmInstanceName?: string;
    lastPostedAt?: string;
    needsLoginSince?: string;
    updatedAt?: string;
}

/**
 * Convert Firestore document data to Session model
 */
function docToSession(docId: string, data: DocumentData): Session {
    return {
        id: docId,
        status: data.status || 'initializing',
        accountUrl: data.accountUrl || data.account_url || '',
        hashtags: data.hashtags,
        vncUrl: data.vncUrl || data.vnc_url,
        vmInstanceName: data.vmInstanceName || data.vm_instance_name,
        progress: data.progress,
        error: data.error,
        createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp
            ? data.updatedAt.toDate().toISOString()
            : data.updatedAt,
    };
}

/**
 * Convert Firestore document data to Comment model
 */
function docToComment(docId: string, data: DocumentData): Comment {
    const selected = data.selected ?? false;
    const declined = data.declined ?? false;

    // Derive status from field or fall back to selected/declined flags
    let status: CommentStatus = (data.status as CommentStatus) || 'underReview';
    if (status === 'underReview') {
        if (selected) status = 'accepted';
        else if (declined) status = 'declined';
    }

    return {
        id: docId,
        postUrl: data.postUrl || data.post_url || '',
        username: data.username || '',
        content: data.content || '',
        imageUrl: data.imageUrl || data.image_url,
        rationale: data.rationale,
        caption: data.caption,
        status,
        selected,
        declined,
        posted: data.posted ?? false,
        postedAt: data.postedAt instanceof Timestamp
            ? data.postedAt.toDate().toISOString()
            : data.postedAt,
        createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt,
    };
}

/**
 * Subscribe to real-time updates for a session document
 *
 * @param accountId - Sanitized account ID (from encodeAccountId)
 * @param sessionId - Session document ID
 * @param onData - Callback for session updates
 * @param onError - Callback for errors
 * @returns Unsubscribe function
 */
export function subscribeToSession(
    accountId: string,
    sessionId: string,
    onData: (session: Session) => void,
    onError: (error: Error) => void
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: accounts/{accountId}/sessions/{sessionId}
    const sessionRef = doc(db, 'accounts', accountId, 'sessions', sessionId);

    return onSnapshot(
        sessionRef,
        (snapshot) => {
            if (snapshot.exists()) {
                const session = docToSession(snapshot.id, snapshot.data());
                onData(session);
            } else {
                onError(new Error('Session not found'));
            }
        },
        (error) => {
            console.error('[FirestoreListener] Session subscription error:', error);
            onError(error);
        }
    );
}

/**
 * Subscribe to real-time updates for comments in a session
 *
 * Uses orderBy createdAt for efficient incremental loading.
 *
 * @param accountId - Sanitized account ID (from encodeAccountId)
 * @param sessionId - Session document ID
 * @param onData - Callback for comments updates (receives all comments)
 * @param onError - Callback for errors
 * @param sinceTimestamp - Optional: only get comments created after this ISO timestamp
 * @returns Unsubscribe function
 */
export function subscribeToComments(
    accountId: string,
    sessionId: string,
    onData: (comments: Comment[]) => void,
    onError: (error: Error) => void,
    sinceTimestamp?: string
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: accounts/{accountId}/sessions/{sessionId}/comments
    const commentsRef = collection(db, 'accounts', accountId, 'sessions', sessionId, 'comments');

    // Build query with orderBy createdAt
    let commentsQuery = query(commentsRef, orderBy('createdAt', 'asc'));

    // Add since filter if provided
    if (sinceTimestamp) {
        const sinceDate = new Date(sinceTimestamp);
        commentsQuery = query(
            commentsRef,
            orderBy('createdAt', 'asc'),
            where('createdAt', '>', Timestamp.fromDate(sinceDate))
        );
    }

    return onSnapshot(
        commentsQuery,
        (snapshot) => {
            const comments: Comment[] = [];
            snapshot.forEach((doc) => {
                comments.push(docToComment(doc.id, doc.data()));
            });
            onData(comments);
        },
        (error) => {
            console.error('[FirestoreListener] Comments subscription error:', error);
            onError(error);
        }
    );
}

/**
 * Convert Firestore document data to AccountInfo model
 */
function docToAccountInfo(docId: string, data: DocumentData): AccountInfo {
    return {
        accountId: docId,
        accountUrl: data.accountUrl || data.account_url || '',
        sessionCount: data.sessionCount ?? 0,
        lastActivity: data.lastActivity instanceof Timestamp
            ? data.lastActivity.toDate().toISOString()
            : data.lastActivity,
    };
}

/**
 * Subscribe to real-time updates for user's triggered accounts
 *
 * Listens to: users/{uid}/instagramAccountsTriggered
 *
 * @param uid - Firebase user ID
 * @param onData - Callback for accounts updates
 * @param onError - Callback for errors
 * @returns Unsubscribe function
 */
export function subscribeToUserAccounts(
    uid: string,
    onData: (accounts: AccountInfo[]) => void,
    onError: (error: Error) => void
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: users/{uid}/instagramAccountsTriggered
    const accountsRef = collection(db, 'users', uid, 'instagramAccountsTriggered');
    const accountsQuery = query(accountsRef, orderBy('lastActivity', 'desc'));

    return onSnapshot(
        accountsQuery,
        (snapshot) => {
            const accounts: AccountInfo[] = [];
            snapshot.forEach((doc) => {
                accounts.push(docToAccountInfo(doc.id, doc.data()));
            });
            console.log('[FirestoreListener] User accounts updated:', accounts.length);
            onData(accounts);
        },
        (error) => {
            console.error('[FirestoreListener] User accounts subscription error:', error);
            onError(error);
        }
    );
}

/**
 * Subscribe to real-time updates for sessions of an account
 *
 * Listens to: accounts/{accountId}/sessions
 *
 * @param accountId - Sanitized account ID
 * @param onData - Callback for sessions updates
 * @param onError - Callback for errors
 * @param sessionsLimit - Max number of sessions to fetch (default: 10)
 * @returns Unsubscribe function
 */
export function subscribeToAccountSessions(
    accountId: string,
    onData: (sessions: Session[]) => void,
    onError: (error: Error) => void,
    sessionsLimit: number = 10
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: accounts/{accountId}/sessions
    const sessionsRef = collection(db, 'accounts', accountId, 'sessions');
    const sessionsQuery = query(
        sessionsRef,
        orderBy('createdAt', 'desc'),
        limit(sessionsLimit)
    );

    return onSnapshot(
        sessionsQuery,
        (snapshot) => {
            const sessions: Session[] = [];
            snapshot.forEach((doc) => {
                sessions.push(docToSession(doc.id, doc.data()));
            });
            console.log(`[FirestoreListener] Account ${accountId} sessions updated:`, sessions.length);
            onData(sessions);
        },
        (error) => {
            console.error(`[FirestoreListener] Account ${accountId} sessions subscription error:`, error);
            onError(error);
        }
    );
}

/**
 * Convert Firestore document data to PendingComment model
 */
function docToPendingComment(docId: string, data: DocumentData): PendingComment {
    return {
        id: docId,
        sessionId: data.sessionId || '',
        postUrl: data.postUrl || data.post_url || '',
        content: data.content || '',
        username: data.username || '',
        queuedAt: data.queuedAt instanceof Timestamp
            ? data.queuedAt.toDate().toISOString()
            : data.queuedAt || '',
        status: data.status || 'pending',
    };
}

/**
 * Subscribe to real-time updates for posting queue metadata
 *
 * Listens to: instagramAccounts/{accountId} -> postingQueueMeta field
 *
 * @param accountId - Encoded account ID
 * @param onData - Callback for metadata updates
 * @param onError - Callback for errors
 * @returns Unsubscribe function
 */
export function subscribeToPostingQueueMeta(
    accountId: string,
    onData: (meta: PostingQueueMeta | null) => void,
    onError: (error: Error) => void
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: instagramAccounts/{accountId}
    const accountRef = doc(db, 'instagramAccounts', accountId);

    return onSnapshot(
        accountRef,
        (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const meta = data.postingQueueMeta as DocumentData | undefined;

                if (meta) {
                    const postingQueueMeta: PostingQueueMeta = {
                        isPostingQueueRunning: meta.isPostingQueueRunning ?? false,
                        status: meta.status || 'idle',
                        vncUrl: meta.vncUrl || meta.vnc_url,
                        vmInstanceName: meta.vmInstanceName || meta.vm_instance_name,
                        lastPostedAt: meta.lastPostedAt instanceof Timestamp
                            ? meta.lastPostedAt.toDate().toISOString()
                            : meta.lastPostedAt,
                        needsLoginSince: meta.needsLoginSince instanceof Timestamp
                            ? meta.needsLoginSince.toDate().toISOString()
                            : meta.needsLoginSince,
                        updatedAt: meta.updatedAt instanceof Timestamp
                            ? meta.updatedAt.toDate().toISOString()
                            : meta.updatedAt,
                    };
                    console.log('[FirestoreListener] Posting queue meta updated:', accountId, postingQueueMeta.status);
                    onData(postingQueueMeta);
                } else {
                    onData(null);
                }
            } else {
                onData(null);
            }
        },
        (error) => {
            console.error('[FirestoreListener] Posting queue meta subscription error:', error);
            onError(error);
        }
    );
}

/**
 * Subscribe to real-time updates for pending comments in the posting queue
 *
 * Listens to: instagramAccounts/{accountId}/postingQueue
 *
 * @param accountId - Encoded account ID
 * @param onData - Callback for comments updates
 * @param onError - Callback for errors
 * @param commentsLimit - Max number of comments to listen to (default: 100)
 * @returns Unsubscribe function
 */
/**
 * Subscribe to real-time updates for a user's credit balance
 *
 * Listens to: users/{uid} document and extracts credits.balance
 *
 * @param uid - Firebase user ID
 * @param onData - Callback with updated credit balance
 * @param onError - Callback for errors
 * @returns Unsubscribe function
 */
export function subscribeToUserCredits(
    uid: string,
    onData: (creditBalance: number) => void,
    onError: (error: Error) => void
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: users/{uid}
    const userRef = doc(db, 'users', uid);

    return onSnapshot(
        userRef,
        (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const credits = data.credits as DocumentData | undefined;
                const balance = credits?.balance ?? 0;
                onData(balance);
            } else {
                onData(0);
            }
        },
        (error) => {
            console.error('[FirestoreListener] User credits subscription error:', error);
            onError(error);
        }
    );
}

/**
 * Subscribe to real-time updates for pending comments in the posting queue
 *
 * Listens to: instagramAccounts/{accountId}/postingQueue
 *
 * @param accountId - Encoded account ID
 * @param onData - Callback for comments updates
 * @param onError - Callback for errors
 * @param commentsLimit - Max number of comments to listen to (default: 100)
 * @returns Unsubscribe function
 */
export function subscribeToPostingQueueComments(
    accountId: string,
    onData: (comments: PendingComment[]) => void,
    onError: (error: Error) => void,
    commentsLimit: number = 100
): Unsubscribe {
    const db = getFirestoreDb();

    // Path: instagramAccounts/{accountId}/postingQueue
    const queueRef = collection(db, 'instagramAccounts', accountId, 'postingQueue');
    const queueQuery = query(
        queueRef,
        orderBy('queuedAt', 'asc'),
        limit(commentsLimit)
    );

    return onSnapshot(
        queueQuery,
        (snapshot) => {
            const comments: PendingComment[] = [];
            snapshot.forEach((doc) => {
                comments.push(docToPendingComment(doc.id, doc.data()));
            });
            console.log(`[FirestoreListener] Posting queue comments updated for ${accountId}:`, comments.length);
            onData(comments);
        },
        (error) => {
            console.error('[FirestoreListener] Posting queue comments subscription error:', error);
            onError(error);
        }
    );
}
